import React, { FormEvent, useMemo, useState } from "react";
import { Clock3, MessageCircle, Plus, Users } from "lucide-react";
import {
  ActivityLog,
  EventCommunication,
  EventOperationsState,
} from "../../types";
import { createEventEntityId } from "../../lib/eventOperations";

interface Props {
  state: EventOperationsState;
  onChange: (state: EventOperationsState) => void;
  onOpenCrm: () => void;
}
const inputClass =
  "w-full h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-300";

export const EventCommunicationsPanel: React.FC<Props> = ({
  state,
  onChange,
  onOpenCrm,
}) => {
  const [composerOpen, setComposerOpen] = useState(false);
  const segments = useMemo(
    () => [
      {
        id: "payment_pending",
        label: "Pagos pendientes",
        count: state.attendees.filter((item) =>
          ["pending", "processing"].includes(item.paymentStatus),
        ).length,
      },
      {
        id: "scholarship_review",
        label: "Becas en revisión",
        count: state.scholarships.filter((item) =>
          ["requested", "under_review"].includes(item.status),
        ).length,
      },
      {
        id: "registered",
        label: "Inscriptos confirmados",
        count: state.attendees.filter((item) =>
          ["registered", "checked_in"].includes(item.registrationStatus),
        ).length,
      },
      {
        id: "checked_in",
        label: "Asistentes presentes",
        count: state.attendees.filter(
          (item) => item.checkInStatus === "checked_in",
        ).length,
      },
      {
        id: "no_show",
        label: "No-show",
        count: state.attendees.filter(
          (item) => item.registrationStatus === "no_show",
        ).length,
      },
    ],
    [state.attendees, state.scholarships],
  );
  const save = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const communication: EventCommunication = {
      id: createEventEntityId("comm"),
      eventId: state.event.id,
      attendeeId: String(data.get("attendeeId") || "") || undefined,
      segment: String(data.get("segment") || "") || undefined,
      channel: String(
        data.get("channel") || "email",
      ) as EventCommunication["channel"],
      subject: String(data.get("subject") || ""),
      body: String(data.get("body") || ""),
      status: "draft",
      createdAt: new Date().toISOString(),
    };
    const activity: ActivityLog = {
      id: createEventEntityId("log"),
      eventId: state.event.id,
      entityType: "communication",
      entityId: communication.id,
      action: "draft_created",
      description: `Borrador ${communication.channel} creado para ${communication.segment || (communication.attendeeId ? "un inscripto" : "audiencia sin definir")}.`,
      createdAt: communication.createdAt,
    };
    onChange({
      ...state,
      communications: [communication, ...state.communications],
      activityLogs: [activity, ...state.activityLogs],
      updatedAt: communication.createdAt,
    });
    setComposerOpen(false);
  };
  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4">
      <section className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display font-black text-slate-950">
                Segmentos operativos
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                Borradores reales; ningún mensaje se envía sin un proveedor
                conectado.
              </p>
            </div>
            <button
              onClick={() => setComposerOpen(!composerOpen)}
              className="h-10 px-3 rounded-xl bg-emerald-500 text-slate-950 text-xs font-black flex items-center gap-2"
            >
              <Plus size={15} /> Borrador
            </button>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 mt-5">
            {segments.map((segment) => (
              <button
                key={segment.id}
                onClick={() => setComposerOpen(true)}
                className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-left hover:border-emerald-300"
              >
                <p className="font-display text-2xl font-black">
                  {segment.count}
                </p>
                <p className="text-xs font-bold text-slate-600 mt-1">
                  {segment.label}
                </p>
              </button>
            ))}
          </div>
        </div>
        {composerOpen && (
          <form
            onSubmit={save}
            className="bg-white border border-slate-200 rounded-2xl p-5 grid sm:grid-cols-2 gap-3"
          >
            <h3 className="font-display font-black sm:col-span-2">
              Nuevo borrador
            </h3>
            <label className="text-[10px] font-black uppercase text-slate-500">
              Segmento
              <select name="segment" className={`${inputClass} mt-1`}>
                <option value="">Individual</option>
                {segments.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label} ({item.count})
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[10px] font-black uppercase text-slate-500">
              Inscripto
              <select name="attendeeId" className={`${inputClass} mt-1`}>
                <option value="">Ninguno / usar segmento</option>
                {state.attendees.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.firstName} {item.lastName}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-[10px] font-black uppercase text-slate-500">
              Canal
              <select name="channel" className={`${inputClass} mt-1`}>
                <option value="email">Email</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option>
                <option value="call">Llamada</option>
              </select>
            </label>
            <label className="text-[10px] font-black uppercase text-slate-500">
              Asunto
              <input name="subject" className={`${inputClass} mt-1`} />
            </label>
            <label className="text-[10px] font-black uppercase text-slate-500 sm:col-span-2">
              Mensaje
              <textarea
                name="body"
                rows={5}
                required
                className={`${inputClass} h-auto py-3 mt-1`}
              />
            </label>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setComposerOpen(false)}
                className="h-10 px-3 rounded-xl border text-xs font-black"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="h-10 px-4 rounded-xl bg-slate-950 text-white text-xs font-black"
              >
                Guardar borrador
              </button>
            </div>
          </form>
        )}
      </section>
      <aside className="space-y-4">
        <div className="bg-slate-950 text-white rounded-2xl p-5">
          <MessageCircle size={24} className="text-emerald-400" />
          <h3 className="font-display font-black mt-4">
            Comunicación conectada
          </h3>
          <p className="text-xs text-slate-300 mt-2">
            Usa CRM para el contacto individual y el journey para
            automatizaciones por etapa.
          </p>
          <button
            onClick={onOpenCrm}
            className="w-full h-10 mt-5 bg-emerald-500 text-slate-950 rounded-xl text-xs font-black"
          >
            Abrir CRM
          </button>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-display font-black text-sm">
              Borradores ({state.communications.length})
            </h3>
          </div>
          <div className="divide-y max-h-96 overflow-y-auto">
            {state.communications.map((item) => (
              <div key={item.id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[9px] font-black uppercase text-emerald-700">
                    {item.channel}
                  </span>
                  <span className="text-[9px] text-slate-400 flex items-center gap-1">
                    <Clock3 size={10} /> {item.status}
                  </span>
                </div>
                <p className="text-xs font-black mt-1">
                  {item.subject || "Sin asunto"}
                </p>
                <p className="text-[10px] text-slate-500 mt-1 line-clamp-2">
                  {item.body}
                </p>
                <p className="text-[9px] text-slate-400 mt-2 flex items-center gap-1">
                  <Users size={10} />{" "}
                  {item.segment ||
                    (item.attendeeId ? "Individual" : "Sin audiencia")}
                </p>
              </div>
            ))}
            {state.communications.length === 0 && (
              <p className="p-5 text-xs text-slate-500 text-center">
                Sin borradores.
              </p>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};
